import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Delete, Edit, RestoreFromTrash } from '@material-ui/icons';
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import { useSnackbar } from 'notistack';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Dialog, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import LinearProgress from '@material-ui/core/LinearProgress';
import DialogActions from '@material-ui/core/DialogActions';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormikTextField from 'components/atoms/FormikInput/FormikInput';
import TablePagination from 'components/atoms/TablePagination/TablePagination';
import Spin from 'components/atoms/Spin/Spin';
import { DictionaryDto, useDictionaryApi } from 'api/dictionary';
import SearchBar from 'components/atoms/SearchBar/SearchBar';
import { Order } from 'utils/sort';
import TableSortCell from 'components/atoms/TablePagination/TableSortCell';
import {
    deactivateReason,
    fetchRejectProducts,
    initRejectReason,
    RejectReasonDto,
} from '../../api/reject-reason';
import { ConfirmContext } from '../../components/atoms/ConfirmProvider/ConfirmProvider';

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 380,
        width: '100%',
    },
    mt5: {
        marginTop: theme.spacing(3),
    },
}));

const RejectReasons = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useContext(ConfirmContext);
    // Table and stuff
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<RejectReasonDto[]>([]);
    const [item, setItem] = useState<RejectReasonDto>(initRejectReason());
    const [products, setProducts] = useState<DictionaryDto[]>([]);
    const [productCodes, setProductCodes] = useState<string[]>([]);

    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof RejectReasonDto>('priority');
    const [search, setSearch] = React.useState<string | null>(null);
    const { getPaged } = useDictionaryApi<RejectReasonDto>('/reject-reason/list');
    const { post, put } = useDictionaryApi<RejectReasonDto>('/reject-reason');

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await getPaged(page, perPage, `${orderBy},${order}`, search);
            setItems(x.content);
            setTotal(x.total_elements);
        } catch {
            enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getPaged, page, perPage, orderBy, order, search, enqueueSnackbar]);

    const [regionOpen, setRegionOpen] = useState(false);

    const handleAdd = () => {
        setItem(initRejectReason());
        setProductCodes([]);
        setRegionOpen(true);
    };

    const handleChange = (event: any) => {
        const {
            target: { value },
        } = event;
        setProductCodes(typeof value === 'string' ? value.split(',') : value);
    };

    const getProducts = useCallback(async () => {
        if (products.length !== 0) {
            return;
        }

        try {
            const x = await fetchRejectProducts();
            setProducts(x.content);
        } catch {
            enqueueSnackbar('Ошибка при получении данных', { variant: 'error' });
        }
    }, [products, enqueueSnackbar]);

    const handleEdit = async (item: RejectReasonDto) => {
        setItem(item);
        const newProductCodes =
            typeof item.products !== 'undefined' ? item?.products?.map((it) => it.code) : [];
        setProductCodes(newProductCodes);
        setRegionOpen(true);
    };

    const handleRequestSort = (property: keyof RejectReasonDto) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleRemove = (id: number | string, status: string) => {
        confirm({}).then(async () => {
            setLoading(true);
            try {
                await deactivateReason(id, status === 'ACTIVE' ? 'DELETED' : 'ACTIVE');
                await fetch();
            } catch {
                enqueueSnackbar('Ошибка при удалении', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        fetch();
    }, [fetch]);

    useEffect(() => {
        getProducts();
    }, [getProducts]);

    return (
        <Box position={'relative'}>
            <SearchBar
                buttonText="Добавить"
                placeholder="Введите текст"
                onButtonClick={() => {
                    handleAdd();
                }}
                onSearchChange={(event: any) => {
                    setSearch(event.target.value);
                }}
            />
            <Box position={'relative'}>
                {loading && <Spin />}
                <TableContainer>
                    <Table size={'small'} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableSortCell
                                    label={'№'}
                                    orderBy={orderBy}
                                    order={order}
                                    property={'id'}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableCell>Код</TableCell>
                                <TableSortCell
                                    label={'Приоритет'}
                                    orderBy={orderBy}
                                    order={order}
                                    property={'priority'}
                                    onRequestSort={handleRequestSort}
                                />
                                <TableCell>Наименование</TableCell>
                                <TableCell>Описание</TableCell>
                                <TableCell>Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index} hover={true}>
                                    <TableCell>{page * perPage + index + 1}</TableCell>
                                    <TableCell>{item.code}</TableCell>
                                    <TableCell>{item.priority}</TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>
                                        {typeof item.products !== 'undefined' &&
                                        item?.products.length > 0
                                            ? item.products.reduce((acc, product) => {
                                                  if (!acc) {
                                                      return product.description;
                                                  }
                                                  return `${acc}, ${product.description}`;
                                              }, '')
                                            : ''}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleEdit(item)}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleRemove(item.id, item.status)}
                                        >
                                            {item.status === 'DELETED' ? (
                                                <RestoreFromTrash />
                                            ) : (
                                                <Delete />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
            <Dialog open={regionOpen} fullWidth={true} onClose={() => setRegionOpen(false)}>
                <Formik
                    enableReinitialize={true}
                    initialValues={item}
                    onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(true);
                        const requestData = {
                            ...values,
                            products: products.filter((elem) => productCodes.includes(elem.code)),
                        };
                        if (values.id) {
                            put(requestData)
                                .then(() => {
                                    setRegionOpen(false);
                                    fetch();
                                })
                                .catch(() =>
                                    enqueueSnackbar('Ошибка при сохранении данных', {
                                        variant: 'error',
                                    })
                                )
                                .finally(() => setSubmitting(false));
                        } else {
                            post(requestData)
                                .then(() => {
                                    setRegionOpen(false);
                                    fetch();
                                })
                                .catch(() =>
                                    enqueueSnackbar('Ошибка при сохранении данных', {
                                        variant: 'error',
                                    })
                                )
                                .finally(() => setSubmitting(false));
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form className={classes.root}>
                            <DialogTitle>Редактирвание причины невручения</DialogTitle>
                            <DialogContent>
                                <Box display="flex" flexDirection="column">
                                    <Field
                                        component={FormikTextField}
                                        className={classes.mt5}
                                        name="code"
                                        label="Код"
                                    />
                                    <Field
                                        component={FormikTextField}
                                        className={classes.mt5}
                                        type="number"
                                        name="priority"
                                        label="Приоритет"
                                    />
                                    <Field
                                        component={FormikTextField}
                                        className={classes.mt5}
                                        name="description"
                                        label="Наименование"
                                    />
                                    <FormControl className={classes.mt5}>
                                        <InputLabel id="products">Продукт</InputLabel>
                                        <Select
                                            labelId="products"
                                            multiple={true}
                                            value={productCodes}
                                            onChange={handleChange}
                                        >
                                            {products.map((product, index) => (
                                                <MenuItem
                                                    key={product.code + index}
                                                    value={product.code}
                                                >
                                                    {product.description}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {isSubmitting && <LinearProgress />}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setRegionOpen(false)}>Отмена</Button>
                                <Button type={'submit'}>Ок</Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </Box>
    );
};

export default RejectReasons;
